import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet, X, Check } from 'lucide-react';
import { useStore } from '../store/useStore';
import { exportHistorialImpresoPDF, exportMallaFormatCSV, exportEvaluacionesCSV, exportMaterialesCSV } from '../logic/exporter';
import { calcularPromedioGeneral, calcularCreditosAcumulados, DEFAULT_ESCALA_FCEA } from '../logic/calculos';
import { cn } from '../lib/utils';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ExportModal({ isOpen, onClose }: ExportModalProps) {
  const { asignaturas, evaluaciones, materiales, escalas } = useStore();
  const [exportedPdf, setExportedPdf] = useState(false);
  const [exportedCsv, setExportedCsv] = useState(false);

  if (!isOpen) return null;

  const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;
  const promedio = calcularPromedioGeneral(asignaturas, evaluaciones, escalaFCEA);
  const creditosTotales = calcularCreditosAcumulados(asignaturas);

  const handleExportPDF = () => {
    exportHistorialImpresoPDF(asignaturas, promedio, creditosTotales);
    setExportedPdf(true);
    setTimeout(() => setExportedPdf(false), 3000);
  };

  const handleExportExcelAll = () => {
    // Exportar asignaturas en el formato exacto de la plantilla de malla curricular
    exportMallaFormatCSV(asignaturas);

    // Exportar evaluaciones y materiales adicionales
    setTimeout(() => exportEvaluacionesCSV(evaluaciones, asignaturas), 400);
    setTimeout(() => exportMaterialesCSV(materiales, asignaturas), 800);

    setExportedCsv(true);
    setTimeout(() => setExportedCsv(false), 3000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 max-w-md w-full p-6 space-y-5 animate-in zoom-in-95 duration-150 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <X size={18} />
        </button>

        <div>
          <h3 className="text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            Exportación de Datos
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Selecciona el formato de exportación para tu información académica y archivos guardados.
          </p>
        </div>

        <div className="space-y-3">
          {/* Opción 1: Exportar a PDF (Documento Resumen / Escolaridad) */}
          <div
            onClick={handleExportPDF}
            className="p-4 rounded-xl border border-slate-200/80 hover:border-blue-300 hover:bg-blue-50/50 transition-all cursor-pointer group flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <FileText size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 group-hover:text-blue-900">
                  Exportar a PDF / Escolaridad Imprimible
                </h4>
                {exportedPdf && <Check size={16} className="text-emerald-600" />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Genera un documento oficial y pulcro con tu resumen de promedio, créditos y desglose de materias aprobadas y en curso.
              </p>
            </div>
          </div>

          {/* Opción 2: Exportar a Excel / CSV (Round-trip Malla Curricular) */}
          <div
            onClick={handleExportExcelAll}
            className="p-4 rounded-xl border border-slate-200/80 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all cursor-pointer group flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <FileSpreadsheet size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 group-hover:text-emerald-950">
                  Exportar a Excel / Plantilla Malla (Round-trip)
                </h4>
                {exportedCsv && <Check size={16} className="text-emerald-600" />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Exporta tus Asignaturas en el <strong>mismo formato exacto</strong> que la plantilla de importación (Nombre, Código, Semestre, Créditos, Departamento, Previas) para permitir editar a mano y reimportar sin fricción.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-2 text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
